// Self-contained Agent (+ Instructions sub-entity + lookup dropdowns) client — targets the same
// real backend routes flow-studio-api's agentApiClient.ts/instructionApiClient.ts already call
// successfully (BizFirst.Ai.AiAgent.Api.Base's BaseAgentController / BaseInstructionController),
// via BaseAdminApiClient instead of flow-studio's workspace-coupled BaseApiClient.
//
// Delete is added here for the first time on the frontend (octopus-admin-spec's confirmed gap:
// agentApiClient.ts has no deleteById at all, even though BaseAgentController.cs:54 already
// implements [HttpDelete("{id:int}")]). Wire shape for delete mirrors credentialApiClient.ts's
// already-working deleteById exactly: `{ id }` spread at root (PascalCased to `Id` on the wire),
// which IDInfo's permissive JSON converter accepts as a bare number.

import { BaseAdminApiClient, type AdminApiConfig, type AdminApiResult } from './baseAdminApiClient';
import type { AgentDTO, AgentFormValues, AgentLookupKey, InstructionDTO, InstructionFormValues, LookupOption } from '../types';
import { AGENT_LOOKUP_ENDPOINTS } from '../types';

export class AgentAdminApiClient extends BaseAdminApiClient {
  constructor(config: AdminApiConfig) {
    super(config);
  }

  /** Active agents, for the picker/list. Mirrors agentApiClient.getActive's pagination default. */
  async getActive(): Promise<AdminApiResult<AgentDTO[]>> {
    const result = await this.postQuery<AgentDTO[]>('/api/v1/ai-agent/agents/active', {
      PageInfo: { PageNumber: 1, PageSize: 1000 },
    });
    return { ...result, data: Array.isArray(result.data) ? result.data : [] };
  }

  async getById(agentId: number): Promise<AdminApiResult<AgentDTO>> {
    return this.postQuery<AgentDTO>('/api/v1/ai-agent/agents/get-by-id', { ID: { ID: agentId } });
  }

  async create(agent: AgentFormValues): Promise<AdminApiResult<AgentDTO>> {
    const response = await this.post<{ id?: { id?: number } }>('/api/v1/ai-agent/agents', agent);
    const newID = response.data?.id?.id;
    if (response.success && newID) {
      return { ...response, data: { AgentID: newID, ...agent } as AgentDTO };
    }
    return response as unknown as AdminApiResult<AgentDTO>;
  }

  async update(agentId: number, agent: Partial<AgentFormValues>): Promise<AdminApiResult<AgentDTO>> {
    const response = await this.put<{ id?: { id?: number } }>(`/api/v1/ai-agent/agents/${agentId}`, {
      AgentID: agentId,
      ...agent,
    });
    if (response.success) {
      return { ...response, data: { AgentID: agentId, ...agent } as AgentDTO };
    }
    return response as unknown as AdminApiResult<AgentDTO>;
  }

  /** NEW — the confirmed frontend gap. Backend route already exists: DELETE /api/v1/ai-agent/agents/{id}. */
  async deleteById(agentId: number): Promise<AdminApiResult<void>> {
    return this.del<void>(`/api/v1/ai-agent/agents/${agentId}`, { id: agentId });
  }

  // --- Instructions (the one sub-entity tab AgentEditor.tsx confirms has real, working delete
  // wired end-to-end already — see agentEditor research: instructionApiClient.deleteById is a
  // real call site, not a stub, unlike the other ~10 sub-entity tabs) ---

  async getInstructionsByAgent(agentId: number): Promise<AdminApiResult<InstructionDTO[]>> {
    const result = await this.postQuery<InstructionDTO[]>('/api/v1/ai-agent/instructions/by-agent-id', {
      AgentID: { ID: agentId },
      PageInfo: { PageNumber: 1, PageSize: 1000 },
    });
    return { ...result, data: Array.isArray(result.data) ? result.data : [] };
  }

  async createInstruction(agentId: number, instruction: InstructionFormValues): Promise<AdminApiResult<InstructionDTO>> {
    return this.post<InstructionDTO>('/api/v1/ai-agent/instructions', { AgentID: agentId, ...instruction });
  }

  // Mirrors instructionApiClient.update's exact body shape (`{ Data: { InstructionID, ...} }`
  // passed into its own post()-equivalent, which nests once more) — replicated literally rather
  // than "corrected", since this is the one sub-entity call confirmed to work end-to-end in
  // production today and there's no independent way to verify a cleaner shape without a live
  // backend call.
  async updateInstruction(instructionId: number, instruction: Partial<InstructionFormValues>): Promise<AdminApiResult<InstructionDTO>> {
    return this.post<InstructionDTO>('/api/v1/ai-agent/instructions/update', {
      Data: { InstructionID: instructionId, ...instruction },
    });
  }

  async deleteInstruction(instructionId: number): Promise<AdminApiResult<void>> {
    return this.post<void>('/api/v1/ai-agent/instructions/delete', { ID: { ID: instructionId } });
  }

  // --- Lookup dropdowns (Category / LLM Configuration / Advanced Settings tabs) ---
  // A plain GET-and-cache, deliberately simpler than flow-studio-core's LookupSelect +
  // lookupEventBus cache-invalidation machinery (workspace-local to flow-studio) — this package
  // doesn't depend on flow-studio at all, so a per-client in-memory cache (cleared only by
  // reloading the page) is the pragmatic v1 tradeoff. Documented in DevelopmentHistoryLog.md.
  private lookupCache = new Map<AgentLookupKey, LookupOption[]>();

  async getLookupOptions(key: AgentLookupKey): Promise<AdminApiResult<LookupOption[]>> {
    const cached = this.lookupCache.get(key);
    if (cached) return { success: true, data: cached };

    const result = await this.postQuery<Array<Record<string, unknown>>>(AGENT_LOOKUP_ENDPOINTS[key], {
      PageInfo: { PageNumber: 1, PageSize: 500 },
    });
    if (!result.success || !Array.isArray(result.data)) {
      return { ...result, data: [] };
    }
    const options: LookupOption[] = result.data.map((item) => {
      const id = (item.ID as number) ?? (item.Id as number) ?? (item[`${key}ID`] as number) ?? 0;
      const name = (item.Name as string) ?? (item.name as string) ?? String(id);
      return { id, name, ...item };
    });
    this.lookupCache.set(key, options);
    return { success: true, data: options };
  }
}
